import { DocumentationTabTypes } from '../../../../types/enums/DocumentationTabTypes';
import { FieldsTable } from '../FieldsTable';
import { useDocumentation } from '../../useDocumentation';
import { getCardListFromConfigurationItem } from '../utils';
import { useSelector } from 'react-redux';
import { hasFieldProperties } from '../../../parser/ConfigurationItemTypes';
import {
  selectSingleObject,
  selectSingleObjectActiveTab,
} from '../../../../reducers/documentation/salesforceDocumentationReducer';
import { selectDocumentationCardHistory } from '../../../../reducers/documentation/documentationCardReducer';
import { useSalesforceCard } from '../../salesforce-documentation-card/hooks/useSalesforceCard';
import { CardsList } from './CardsList';
import { selectCrmOrgHasManagePackage } from '../../../pages/environments/environmentsReducer';
import { MenuElementsWithBadge } from './MenuElementsWithBadge';
import { SalesforceItem } from '../../../parser/ParserTypes';
import { SelectedObjectListElements } from '../../types';
import { MetadataCards } from './MetadataCards';

interface SelectedObjectCardsProps {
  crmOrgId: string;
  items: SalesforceItem[] | SelectedObjectListElements;
}

export const SelectedObjectCards = ({ crmOrgId, items }: SelectedObjectCardsProps) => {
  const objectTypeName = useSelector(selectSingleObject);
  const activeTab = useSelector(selectSingleObjectActiveTab);
  const isManagePackageInstalled = useSelector(selectCrmOrgHasManagePackage(crmOrgId));
  const { onTabChange } = useDocumentation();
  const { handleSetConfigurationItem } = useSalesforceCard(crmOrgId);
  const cardHistory = useSelector(selectDocumentationCardHistory);
  const firstHistoryItemMetadataId =
    cardHistory.length > 0 && cardHistory[0].platform === 'salesforce'
      ? cardHistory[0].item.metadataId
      : undefined;

  if (!objectTypeName) {
    return <MetadataCards crmOrgId={crmOrgId} list={items as SalesforceItem[]} />;
  }

  if (!objectTypeName || !activeTab) return null;

  const itemsAsConfigurationItems = items as SalesforceItem[];

  const renderActiveTab = () => {
    switch (activeTab) {
      case DocumentationTabTypes.CARDS_LIST:
        return (
          <MenuElementsWithBadge
            elements={items as SelectedObjectListElements}
            onClick={onTabChange}
          />
        );

      case DocumentationTabTypes.FIELDS:
        return (
          <FieldsTable
            fields={itemsAsConfigurationItems.filter(hasFieldProperties)}
            isManagePackageInstalled={!!isManagePackageInstalled}
            handleSetConfigurationItem={handleSetConfigurationItem}
            activeMetadataId={firstHistoryItemMetadataId}
          />
        );

      case DocumentationTabTypes.RECORD_TYPES:
        return (
          <CardsList
            list={itemsAsConfigurationItems}
            activeMetadataId={firstHistoryItemMetadataId}
            handleSetConfigurationItem={handleSetConfigurationItem}
          />
        );

      case DocumentationTabTypes.RELATED_OBJECTS:
        return (
          <CardsList
            list={getCardListFromConfigurationItem(
              itemsAsConfigurationItems,
              objectTypeName.objectType,
            )}
            activeMetadataId={firstHistoryItemMetadataId}
            handleSetConfigurationItem={handleSetConfigurationItem}
            withDetailedContentForRelatedObjects
            withApiNameOnHover
          />
        );

      default:
        return (
          <CardsList
            list={getCardListFromConfigurationItem(
              itemsAsConfigurationItems,
              objectTypeName.objectType,
            )}
            activeMetadataId={firstHistoryItemMetadataId}
            handleSetConfigurationItem={handleSetConfigurationItem}
          />
        );
    }
  };

  return renderActiveTab();
};
